import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import TooltipConfigEditor from "components/dashboard/tooltip-config-editor/TooltipConfigEditor";
import { InputSwitch } from "primereact/inputswitch";
import React, { FC, useEffect } from "react";
import { T } from "services/lang/LanguageService";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { DropdownChangeEvent } from "primereact/dropdown";
import { ComponentTheme, DweenTimeUnitEnum } from "utils/enums";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Divider } from "primereact/divider";
import { DatasetStatusControllerProps } from "../DatasetStatusControllerTypes";

const defaultDatasetStatusControllerConfig: DatasetStatusControllerProps = {
  config: {
    alignStartTo: DweenTimeUnitEnum.HOURS,
    timeOffset: 0,
    timeOffsetUnits: DweenTimeUnitEnum.HOURS,
    dataFrequency: DweenTimeUnitEnum.HOURS,
    updateFrequency: 5,
  },
  identifier: "",
  theme: ComponentTheme.TRANSPARENT,
};

const DatasetStatusControllerEditor: FC<DashboardEditorProps<DatasetStatusControllerProps>> = ({
  config,
  onConfigChange,
}) => {
  useEffect(() => {
    if (config && !Object.keys(config).length) {
      onConfigChange(defaultDatasetStatusControllerConfig);
    }
  }, []);

  const handleConfigUnitsChange = (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    onConfigChange({
      ...config,
      config: {
        ...config.config,
        [e.target.id]: e.target.value?.toString().toUpperCase(),
      },
    });
  };

  const handleConfigNumbersChange = (e: InputNumberValueChangeEvent) => {
    onConfigChange({
      ...config,
      config: {
        ...config.config,
        [e.target.id]: e.target.value,
      },
    });
  };

  const handleDatasetChange = (value: string | null) => {
    onConfigChange({ ...config, identifier: value ?? "" });
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-row gap-3">
        <div className="flex flex-column gap-2 w-10">
          <label htmlFor="valueDataset">{T("word.dataset")}</label>
          <DatasetDropdown value={config.identifier ?? ""} onChange={handleDatasetChange} />
        </div>
        <div className="flex flex-column gap-2 w-2">
          <label htmlFor="useTooltip">{T("word.use-tooltip")}</label>
          <InputSwitch
            id="useTooltip"
            checked={config.useTooltip ?? false}
            onChange={e => onConfigChange({ ...config, useTooltip: e.value })}
          />
        </div>
      </div>
      <Divider className="m-0" />
      <div className="flex w-12 justify-content-start">
        <div className="col-4 gap-2 flex flex-column">
          <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
          <div className="p-inputgroup">
            <InputNumber
              id="timeOffset"
              className="w-6"
              value={config.config?.timeOffset as number}
              onValueChange={handleConfigNumbersChange}
            />
            <TimeUnitDropdown
              id="timeOffsetUnits"
              className="p-inputgroup-addon p-0 w-6"
              value={config.config?.timeOffsetUnits ? config.config.timeOffsetUnits : ""}
              onChange={handleConfigUnitsChange}
            />
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column">
          <label htmlFor="dataFrequency">{T("config.data-frequency")}</label>
          <TimeUnitDropdown
            id="dataFrequency"
            className="p-0 w-full"
            value={config.config?.dataFrequency ? config.config.dataFrequency : ""}
            onChange={handleConfigUnitsChange}
          />
        </div>
        <div className="col-2 gap-2 flex flex-column">
          <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
          <TimeUnitDropdown
            id="alignStartTo"
            className="p-0 w-full"
            value={config.config?.alignStartTo ? config.config.alignStartTo : ""}
            onChange={handleConfigUnitsChange}
          />
        </div>
        <div className="col-2 gap-2 flex flex-column">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              className="p-0 w-full"
              value={config.config?.updateFrequency as number}
              onValueChange={handleConfigNumbersChange}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
      </div>
      {config.useTooltip && (
        <>
          <Divider className="m-0" />
          <TooltipConfigEditor config={config} onChange={onConfigChange} />
        </>
      )}
    </div>
  );
};

export default DatasetStatusControllerEditor;
